import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { submitTicket, fetchSupportCategories } from '../services/support';
import '../styles/FeedbackWidget.css';

export default function FeedbackWidget() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [agreeScreenshot, setAgreeScreenshot] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dialogRef = useRef(null);

  const handleOpen = async () => {
    dialogRef.current.showModal();
    if (categories.length === 0) {
      try {
        const cats = await fetchSupportCategories();
        setCategories(cats);
      } catch {
        setCategories([]);
      }
    }
  };

  const handleClose = () => {
    dialogRef.current.close();
    setMessage('');
    setCategory('');
    setAgreeScreenshot(false);
    setShowErrors(false);
    setSubmitting(false);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !message || !agreeScreenshot) {
      setShowErrors(true);
      return;
    }

    setSubmitting(true);

    try {
      const canvas = await html2canvas(document.body);
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      );

      await submitTicket({
        subject: 'Feedback',
        category,
        message,
        screenshot: blob,
      });

      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      setSubmitting(false);
      alert('Failed to submit feedback, please try again.');
    }
  };

  const isFormValid = category && message && agreeScreenshot;

  return (
    <>
      <button className="feedbackpopup-toggle" onClick={handleOpen} aria-label="Open feedback">
        <svg className="feedbackpopup-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.4 8.4 0 0 1-8.8 8.4 9.1 9.1 0 0 1-3.8-.8L3 20.5l1.5-4.8a8 8 0 0 1-.9-3.8A8.4 8.4 0 0 1 12 3.5a8.7 8.7 0 0 1 9 8Z" />
          <path d="M8 10h8" />
          <path d="M8 14h5" />
        </svg>
        <span className="feedbackpopup-sr-only">Feedback</span>
      </button>

      <dialog
        ref={dialogRef}
        className="feedbackpopup-dialog"
        onCancel={(e) => {
          if (!submitting) handleClose();
          else e.preventDefault(); // block Esc close while submitting
        }}
        onClick={(e) => {
          if (!submitting && e.target === dialogRef.current) {
            handleClose(); // click backdrop closes before submit
          }
        }}
      >
        {submitting && (
          <div className="feedbackpopup-overlay">
            <div className="feedbackpopup-spinner"></div>
            <p>Submitting your feedback...</p>
          </div>
        )}

        {!submitted ? (
          <form method="dialog" className="feedbackpopup-form" onSubmit={handleSubmit}>
            <h2>Submit Feedback</h2>

            <label>
              Category
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={!category && showErrors ? 'feedbackpopup-error' : ''}
                required
              >
                <option value="">Select...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {!category && showErrors && (
                <span className="feedbackpopup-error-text">Please select a category</span>
              )}
            </label>

            <label>
              Describe the issue
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={!message && showErrors ? 'feedbackpopup-error' : ''}
                required
              />
              {!message && showErrors && (
                <span className="feedbackpopup-error-text">Message is required</span>
              )}
            </label>

            {/* Agreement Row */}
            <div className="feedbackpopup-agree-row">
              <input
                type="checkbox"
                checked={agreeScreenshot}
                onChange={(e) => setAgreeScreenshot(e.target.checked)}
                className={!agreeScreenshot && showErrors ? 'feedbackpopup-error-checkbox' : ''}
              />
              <span>I agree that a screenshot will be taken with my feedback.</span>
            </div>
            {!agreeScreenshot && showErrors && (
              <span className="feedbackpopup-error-text">You must agree before submitting</span>
            )}

            <menu>
              <button
                type="button"
                className="feedbackpopup-btn feedbackpopup-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>

              <button
                type="submit"
                className={`feedbackpopup-btn feedbackpopup-submit ${!isFormValid ? 'invalid' : ''}`}
                onClick={(e) => {
                  if (!isFormValid) {
                    e.preventDefault();
                    setShowErrors(true);
                  }
                }}
              >
                Submit
              </button>
            </menu>
          </form>
        ) : (
          <div className="feedbackpopup-thanks">
            <h2>Thank you!</h2>
            <p>Your feedback has been submitted successfully.</p>
            <button className="feedbackpopup-btn feedbackpopup-cancel" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </dialog>
    </>
  );
}
